<template>
  <VModal :showing="showing" max-content="calc(100vh - 225px)" container-classes="max-w-200" @update:showing="cancel" @esc="cancel">
    <template #header>
      <h2 class="text-lg m-0">{{ action === 'create' ? 'Select Pages' : 'Update PDF Slide' }}</h2>
      <div class="text-sm text-gray-600 font-normal">
        {{ action === 'create' ? 'Create PDF Slide' : 'Select a page to replace your current PDF Slide' }}
      </div>
    </template>
    <template #default>
      <div class="flex gap-8">
        <div v-if="loading" class="left-side flex justify-center items-center w-100 h-100">
          <FontAwesomeIcon class="text-gray-100" icon="circle-notch" spin size="3x" />
        </div>
        <div v-else class="left-side">
          <div class="w-100 min-h-56 border border-gray-100 flex justify-center items-center">
            <img v-if="thumbnailSrc" :src="thumbnailSrc" data-testid="pdf-thumbnail" />
            <div v-else class="text-gray-300">No page selected</div>
          </div>
          <div class="flex w-full items-center justify-between mt-6 pt-6 border-t border-gray-300">
            <div class="whitespace-nowrap">
              <VButton class="mr-1" :disabled="currentPage === 1" size="sm" @click="currentPage = 1">
                <FontAwesomeIcon icon="chevron-left" transform="right-2" />
                <FontAwesomeIcon icon="chevron-left" transform="left-3" />
              </VButton>
              <VButton :disabled="currentPage === 1" size="sm" @click="currentPage = Math.max(1, currentPage - 1)">
                <FontAwesomeIcon icon="chevron-left" />
              </VButton>
            </div>
            <div class="whitespace-nowrap flex items-center">
              <VInput
                :model-value="`${currentPage || ''}`"
                class="mr-2 text-center"
                size="sm"
                :class="{ 'v-input__error': !currentPageIsValid }"
                :style="{ width: currentPageInputWidth }"
                @input="setCurrentPage"
              />
              of {{ images.length }}
            </div>
            <div class="whitespace-nowrap">
              <VButton class="mr-1" :disabled="currentPage === images.length" size="sm" @click="currentPage += 1">
                <FontAwesomeIcon icon="chevron-right" />
              </VButton>
              <VButton :disabled="currentPage === images.length" size="sm" @click="currentPage = images.length">
                <FontAwesomeIcon icon="chevron-right" transform="right-3" />
                <FontAwesomeIcon icon="chevron-right" transform="left-2" />
              </VButton>
            </div>
          </div>
        </div>
        <div class="right-side text-sm text-gray-700">
          <label class="font-medium" for="pageNumbers">{{ action === 'create' ? 'Select Pages' : 'Select a Page' }}</label>
          <VInput
            id="pageNumbers"
            v-model="pageNumbers.raw"
            :error="pageNumbers.error"
            :min="0"
            :placeholder="action === 'create' ? 'e.g. 1, 3-5, 8' : 'e.g. 1'"
            data-testid="page-range-input"
            :type="action === 'create' ? 'text' : 'number'"
          />
          <div v-if="pageNumbers.error" class="error mt-2 text-red-800">
            <template v-if="pageNumbers.parsed === null">Please enter a valid page list.</template>
            <template v-else-if="Math.max(...pageNumbers.parsed) > images.length">The maximum page number is {{ images.length }}.</template>
            <template v-else-if="Math.min(...pageNumbers.parsed) <= 0">The minimum page is 1.</template>
            <template v-else>Please enter a valid page list.</template>
          </div>
          <div v-if="action === 'create'" class="my-4">
            <p class="mb-0">To import a range of pages, use a hyphen (eg: 1-10)</p>
            <p>To import a selection of pages, use a comma (eg: 1, 2, 6)</p>
            <p>All slide titles will be automatically named</p>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex justify-end gap-2">
        <VButton variant="light" @click="$emit('back')">Back</VButton>
        <VButton variant="copper" data-testid="next-step" @click="goNext">
          <template v-if="action === 'create'">{{ pageNumbers.parsed && pageNumbers.parsed.length === 1 ? 'Next' : 'Create' }}</template>
          <template v-else>Update</template>
        </VButton>
      </div>
    </template>
  </VModal>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { computed, defineComponent, reactive, ref, toRef, watch } from 'vue'

import type { ShelfFile } from '@/entities/shelfFile'
import type { SlideImage } from '@/entities/deck'
import { parseRanges } from '@/lib/utils'

const props = defineProps({
  showing: {
    type: Boolean,
    required: true,
  },
  pdfFile: {
    type: Object as PropType<ShelfFile | null>,
    default: null,
  },
  action: {
    type: String,
    default: 'create',
  },
})

const emit = defineEmits(['close', 'back', 'choose-pages'])

const { $sentry } = useNuxtApp()

const images = ref<SlideImage[]>([])
const currentPage = ref(1)
const currentPageIsValid = computed(() => !isNaN(currentPage.value) && 0 < currentPage.value && currentPage.value <= images.value.length)
const currentPageInputWidth = computed(() => `${(currentPage.value !== null ? `${currentPage.value}`.length : 0) * 0.6 + 2}rem`)
const thumbnailSrc = computed(() => images.value[currentPage.value - 1]?.url)
const pageNumbers = reactive({ raw: '', error: false, parsed: null as number[] | null })
const loading = ref(false)

watch(
  toRef(props, 'pdfFile'),
  async pdf => {
    if (pdf) {
      loading.value = true
      try {
        const data = await $fetch<any>(`v2/companies/${pdf.company_id}/shelf/files/${pdf.id}/previews`, {
          ...useFetchSharedOptions(),
        })

        images.value = data
        pageNumbers.raw = props.action === 'create' ? `1-${data.length}` : '1'
        currentPage.value = 1
      } catch (e) {
        $sentry?.captureException(e)
      } finally {
        loading.value = false
      }
    }
  },
  { immediate: true }
)

watch(toRef(pageNumbers, 'raw'), () => {
  // clear error on new input
  pageNumbers.error = false
  pageNumbers.parsed = parseRanges(pageNumbers.raw)
})

watch(toRef(pageNumbers, 'parsed'), () => {
  const isValid = pageNumbers.parsed?.[0] && images.value[pageNumbers.parsed?.[0] - 1]?.url
  // Typescript is complaining about pageNumbers.parsed?.[0] being a number | null
  if (isValid) currentPage.value = <number>pageNumbers.parsed![0]
})

function cancel() {
  emit('close')
}

function goNext() {
  const parsed = (pageNumbers.parsed = parseRanges(pageNumbers.raw))

  if (parsed && parsed.length > 0 && Math.max(...parsed) <= images.value.length && Math.min(...parsed) > 0) {
    emit('choose-pages', parsed)
  } else {
    pageNumbers.error = true
  }
}

function setCurrentPage(e: Event) {
  const value = (e.target as HTMLInputElement).value
  currentPage.value = (value && !isNaN(+value) ? +value : null) as number
}
</script>
